import { Box, Card, BodyText, AccountFundingOption, FetchFundingOptionsResponse } from 'vopay';
import { useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAlertDialog, useBlockingLoader, useDialog, useToast } from 'shared';
import { EventMessageData } from 'shared/eLinx';
import DefaultFundingMethods from './DefaultFundingMethods/DefaultFundingMethods';
import AddOperationalDialog, { AddOperationalSelection } from './AddOperationalDialog';
import { api } from '@/services/api.config';
import useELinxConnect from '@/hooks/useELinxConnect';
import { useNavMenu } from '@/providers/NavMenu.provider';
import { useClientData } from '@/providers/ClientData.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { useOpenModule } from '@/providers/OpenModule.provider';
import { useAccountInfo } from '@/providers/AccountInfo.provider';
import useFetchDefaultCreditCard from '@/hooks/useApi/useFetchDefaultCreditCard';
import useFetchFundingOptions from '@/hooks/useApi/useFetchFundingOptions';
import {
  AccountListingPanel,
  AccountRow,
  AccountSearchParams,
  BankAccountRow,
  buildAccountRows,
  CREDIT_CARD_CURRENCY,
  getAccountHolderLabel,
  getAccountRowToken,
  getMaskedAccountLabel,
  MyAccountType,
  useAccountListing,
  useAccountTypeLabels,
  useFetchBankAccounts,
  useFetchClientCreditCards,
  useFetchDefaultBankAccount,
  useFetchDefaultPaymentMethod,
  useSetClientBankAccount,
  useSetClientCreditCard,
} from 'shared/accountAndBilling';
import useBillingHostProps from '@/components/Accounts/AccountAndBilling/useBillingHostProps';

import { Button, Title } from 'UI';

/**
 * The account a confirm dialogue names and the save then acts on. Flattened out of the row (or out of
 * the eLinx payload plus the refetched list) at the point the flow starts, so the dialogue copy and the
 * request both read from one value rather than from loose number/type/name/currency state.
 */
type OperationalTarget = {
  type: MyAccountType;
  currency: string;
  token: string;
  maskedNumber: string;
  holderName: string;
};

export default function OperationalAccountsPage() {
  const { viewer } = useBillingHostProps();
  const { t, i18n } = useTranslation('translations', { keyPrefix: 'Accounts.OperationalAccounts' });

  // The dialogue copy keeps its own key namespace even though the strings are passed from the awaited
  // calls below rather than from a dialogue component.
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { t: tButtons } = useTranslation('translations', { keyPrefix: 'Global.Buttons' });

  const { t: tDialogue } = useTranslation('translations', {
    keyPrefix: 'Accounts.OperationalAccountDialogues',
  });

  const { getAccountTypeLabel } = useAccountTypeLabels();
  const history = useHistory();
  const blockingLoader = useBlockingLoader();
  const toast = useToast();
  const alertDialog = useAlertDialog();
  const dialog = useDialog();
  const eLinxConnect = useELinxConnect();
  const { response } = useClientData();
  const isFranchise = response.SubAccountType === 'franchise';
  const { getModuleTitle } = useNavMenu();

  // The account-wide default payment method is Billing's, not this page's — it is read here only to
  // mark a card row as the billing default, which is what stops the drawer offering to delete it.
  const { defaultPaymentMethod, fetchDefaultPaymentMethod } = useFetchDefaultPaymentMethod(
    api.fetchDefaultPaymentMethod,
  );

  const {
    status: defaultBankAccountStatus,
    defaultOperationalBankAccount,
    defaultBillingBankAccount,
    fetchDefaultBankAccount,
  } = useFetchDefaultBankAccount();

  const { setClientBankAccount } = useSetClientBankAccount();
  const { setClientCreditCard } = useSetClientCreditCard();

  const {
    defaultOperationalCreditCard,
    fetching: isFetchingDefaultCreditCard,
    fetchDefaultCreditCard,
  } = useFetchDefaultCreditCard();

  const { fetching: isFetchingFundingOptions, fetchFundingOptions } = useFetchFundingOptions();
  const [fundingOptions, setFundingOptions] = useState<AccountFundingOption[]>([]);
  const { permissions } = usePermissions();

  const { setOpenModule } = useOpenModule();

  const { creditCards, status: creditCardsStatus, fetchCreditCards } = useFetchClientCreditCards();

  const { bankAccounts, status: bankAccountsStatus, fetchBankAccounts } = useFetchBankAccounts();
  const { setNewCurrencyInfo } = useAccountInfo();

  // Every collection the page renders from, derived rather than written into state by an effect.
  // Operational resolves its default flags off the default *operational* bank account and the default
  // operational credit card; a row's billing flag only marks it, it is never set from here.
  const accountRows = useMemo(
    () =>
      buildAccountRows({
        bankAccounts,
        creditCards,
        purpose: 'Operational',
        country: response.Country,
        resolveBankDefaults: (bankAccount) => {
          const isOperationalDefault = defaultOperationalBankAccount.some((obj) => obj.Token === bankAccount.Token);

          return {
            IsDefault: isOperationalDefault,
            IsOperationalDefault: isOperationalDefault,
            IsBillingDefault: defaultBillingBankAccount.some((obj) => obj.Token === bankAccount.Token),
          };
        },
        resolveCardDefaults: (creditCard) => {
          const isOperationalDefault =
            !!creditCard.CreditCardToken &&
            creditCard.CreditCardToken === defaultOperationalCreditCard?.CreditCardToken;

          return {
            IsDefault: isOperationalDefault,
            IsOperationalDefault: isOperationalDefault,
            IsBillingDefault: defaultPaymentMethod === 'credit' && creditCard.IsDefault === '1',
          };
        },
      }),
    [
      bankAccounts,
      creditCards,
      response.Country,
      defaultOperationalBankAccount,
      defaultBillingBankAccount,
      defaultOperationalCreditCard,
      defaultPaymentMethod,
    ],
  );

  // Derived at render, not in the catch: a catch can only read the state of the render that fired the
  // request, which on mount is always the empty one, so it cannot tell "nothing loaded" from "some of
  // it did". Credit cards load separately, so a failed bank fetch can still leave rows on screen.
  //
  // The credit cards count too: a separate fetch that can fail on its own, leaving the rows short.
  const hasFetchFailed = bankAccountsStatus === 'error' || creditCardsStatus === 'error';
  const isShowingLoadError = hasFetchFailed && accountRows.purposeRows.length === 0;
  const isShowingRefreshError = hasFetchFailed && accountRows.purposeRows.length > 0;

  // The drawer resolves its row against every row on the account: it also opens from the
  // default-funding cards, which show accounts the table may not be listing.
  const listing = useAccountListing({ rows: accountRows.purposeRows, detailRows: accountRows.allRows, search });
  const { currency } = listing.queryParams;

  // Accounts the add-operational dialogue offers: the bank accounts that aren't operational yet.
  const selectableBankAccounts = useMemo(
    () =>
      accountRows.otherRows.filter(
        (row): row is BankAccountRow => row.Type === 'Bank Account' && !row.Purpose?.split(',').includes('Operational'),
      ),
    [accountRows],
  );

  // Adds the account to the operational list, optionally as the default, and refreshes everything the
  // change moves. isSavingDefault / isAddingAccount arrive as arguments rather than being read back off
  // state: the flow awaits, so a closure over flags set in the same click would see their pre-render
  // values. They also decide which success dialogue the save earns — none at all when an existing
  // default is merely being changed. The confirm dialogue is already gone by the time this runs (its
  // promise resolved), so a failure has nothing left to close and reports through a toast instead.
  async function setAccountDefault(
    asDefault: boolean,
    target: OperationalTarget,
    { isSavingDefault, isAddingAccount }: { isSavingDefault: boolean; isAddingAccount: boolean },
  ) {
    const isCard = target.type === 'Credit Card';

    blockingLoader.show();

    try {
      if (isCard) {
        await setClientCreditCard({ CreditCardToken: target.token, SetAsDefault: asDefault, Purpose: 'Operational' });
        await refreshCreditCards();
        // Purpose sent above was scoped to this listing type, so a Billing-page change can't affect the
        // Operational default — and only Operational renders it.
        await refreshDefaultCreditCard();
      } else {
        await setClientBankAccount({ Token: target.token, SetAsDefault: asDefault, Purpose: 'Operational' });
        await refreshBankAccounts();
      }

      await refreshDefaultBankAccount(currency);
    } catch (error) {
      // The hook already logged; the call site's job is to tell the user what the backend said.
      const errorMessage =
        error instanceof Error
          ? error.message
          : tGlobal('errorOccurred', { defaultValue: 'An error occurred. Please try again later.' });

      // neutral, not success: this provider has no error variant (see Toast.provider.tsx).
      toast.neutral(
        t('Toast.saveDefaultError', {
          errorMessage,
          defaultValue: "We couldn't save your operational account: {{errorMessage}}",
        }),
      );

      return;
    } finally {
      blockingLoader.hide();
    }

    // Outside the try/finally so the success dialogue isn't raised behind the blocking loader.
    const accountType = getAccountTypeLabel(target.type);

    if (!isSavingDefault) {
      await alertDialog.success(
        tDialogue('AddSuccess.accountAdded', { defaultValue: 'Account Successfully Added' }),
        tDialogue('AddSuccess.successMessage', {
          accountName: target.holderName,
          accountType,
          defaultValue: '{{accountName}} {{accountType}} has been successfully added as a operational account.',
        }),
        { confirmLabel: tButtons('okay') },
      );

      return;
    }

    if (isAddingAccount) {
      await alertDialog.success(
        tDialogue('ChangeDefaultSuccess.title', { defaultValue: 'Default Successfully Changed' }),
        tDialogue('ChangeDefaultSuccess.subTitle', {
          accountName: target.holderName,
          accountType,
          currency: target.currency,
          defaultValue:
            '{{accountName}} {{accountType}} has been successfully added as your {{currency}} operational default account.',
        }),
        { confirmLabel: tButtons('okay') },
      );
    }
  }

  // Not the bank accounts or their defaults: the submit-search effect below also runs on mount, and
  // its fetch is the same pair of calls with the URL's filters applied.
  useEffect(() => {
    const load = async () => {
      try {
        await Promise.all([refreshCreditCards(), fetchDefaultPaymentMethod(), refreshDefaultCreditCard()]);
      } catch {
        // The hooks already logged, and a failed load leaves the page's own empty states in place.
      }
    };

    load();
  }, []);

  // The refreshes hand back the lists they fetched, not just the hooks' state: the eLinx add flow has to
  // act on what it just fetched, and it is still in the render that fired the request, where
  // bankAccounts/creditCards hold the pre-fetch values.
  // Every bank-account fetch goes through here, so a refresh after a save, an add or a delete asks
  // for the same slice the last search did. Fetching unfiltered instead repopulated the table with
  // accounts of every currency while the filter chip still showed one — useAccountListing re-applies
  // type and keyword client-side, but currency is a request parameter it cannot re-apply.
  function buildBankAccountParams({ currency: forCurrency, searchTerm }: AccountSearchParams) {
    return {
      ClientControlled: true,
      Token: '',
      Timezone: viewer.timezone,
      ...(forCurrency === 'All' ? {} : { Currency: forCurrency }),
      SearchText: searchTerm,
    };
  }

  async function refreshBankAccounts() {
    const res = await fetchBankAccounts(buildBankAccountParams(listing.queryParams));

    return res.BankAccounts;
  }

  async function refreshCreditCards() {
    const res = await fetchCreditCards({ ClientControlled: true });

    return res.CreditCards;
  }

  // Timezone and the All-currency convention are identical on every default-bank-account call.
  // Same surface as refreshDefaultCreditCard, for the same reason: both feed the funding cards, and a
  // failure here means those are unreadable, not that whatever prompted the refresh failed. Catching
  // here also keeps a post-save refresh failure from being reported as a failed save.
  async function refreshDefaultBankAccount(forCurrency: string) {
    try {
      await fetchDefaultBankAccount({
        Timezone: viewer.timezone,
        ...(forCurrency === 'All' ? {} : { Currency: forCurrency }),
      });
    } catch {
      toast.neutral(
        t('FundingMethod.fetchError', {
          defaultValue: 'Could not load default funding methods.',
        }),
      );
    }
  }

  // Its own surface rather than the caller's: only the card behind the funding cards goes stale, so a
  // failure here says the funding methods are unreadable, not that whatever prompted the refresh failed.
  async function refreshDefaultCreditCard() {
    try {
      await fetchDefaultCreditCard();
    } catch {
      toast.neutral(
        t('FundingMethod.fetchError', {
          defaultValue: 'Could not load default funding methods.',
        }),
      );
    }
  }

  // Page data: the listing renders a skeleton from the fetch status. loading-conventions reserves the
  // fullscreen loader for flows the user must not interact through — the saves, the deletes and the
  // eLinx post-connect refresh, not reloading a filtered list.
  async function search(searchParams: AccountSearchParams) {
    try {
      await Promise.all([
        fetchBankAccounts(buildBankAccountParams(searchParams)),
        refreshDefaultBankAccount(searchParams.currency),
      ]);
    } catch {
      // The hooks already logged, and the listing keeps the accounts from the last good fetch.
    }
  }

  useEffect(() => {
    listing.submitSearch(listing.queryParams);
  }, []);

  function toOperationalTarget(account: AccountRow): OperationalTarget {
    return {
      type: account.Type,
      currency: account.Currency,
      token: getAccountRowToken(account),
      maskedNumber: getMaskedAccountLabel(account),
      holderName: getAccountHolderLabel(account),
    };
  }

  async function confirmChangeDefault(account: AccountRow) {
    const target = toOperationalTarget(account);

    const result = await alertDialog.open(
      tDialogue('ChangeDefault.title', { defaultValue: 'Change Default Operational Account' }),
      tDialogue('ChangeDefault.subTitle', {
        accountNumber: target.maskedNumber,
        accountType: getAccountTypeLabel(target.type),
        currency: target.currency,
        defaultValue:
          'Please confirm {{accountNumber}} {{accountType}} to be selected as the {{currency}} default operational account.',
      }),
      {
        abortLabel: tButtons('cancel'),
        confirmLabel: tDialogue('ChangeDefault.confirmLabel', { defaultValue: 'Set As Default' }),
      },
    );

    if (result !== 'confirm') return;

    await setAccountDefault(true, target, { isSavingDefault: true, isAddingAccount: false });
  }

  // The choice dialogue every add flow ends at, reached both from the pick-an-existing-account dialog
  // and straight from eLinx. Its two buttons are one question — add, or add and make it the default — so
  // they are the alert's abort/confirm, and the X (or Escape) is what means "never mind". A credit card
  // cannot be made the operational default, so it gets the single-button variant.
  async function confirmAddToOperational(target: OperationalTarget) {
    const isCard = target.type === 'Credit Card';
    const accountType = getAccountTypeLabel(target.type);

    const addLabel = tDialogue('AddType.addOperationalAccount', {
      defaultValue: 'Add Operational Account',
    });

    const result = await alertDialog.open(
      addLabel,
      tDialogue('AddType.confirmation', {
        accountNumber: target.maskedNumber,
        accountType,
        defaultValue: 'Please confirm {{accountNumber}} {{accountType}} to add it to the operational account list.',
      }),
      {
        abortLabel: isCard ? undefined : addLabel,
        confirmLabel: isCard
          ? addLabel
          : tDialogue('AddType.addAndSetAsDefault', { defaultValue: 'Add and Set as Default' }),
        // Both buttons add the account, so the X is the only way to back out of the flow entirely.
        closeButton: true,
      },
    );

    if (result === 'close') return;

    const isSettingAsDefault = !isCard && result === 'confirm';

    await setAccountDefault(isSettingAsDefault, target, {
      isSavingDefault: isSettingAsDefault,
      isAddingAccount: true,
    });
  }

  // The pick-an-existing-account dialogue is the one that returns a value, so it is a useDialog() call
  // rather than an alert: it resolves either the token the user picked or a request to connect a new
  // account with the payment method they chose, and closes itself either way — which is what keeps it
  // from sitting behind the eLinx overlay.
  async function startAddOperational() {
    const selection = await dialog.open<AddOperationalSelection>(
      (dialogProps) => <AddOperationalDialog {...dialogProps} selectableAccounts={selectableBankAccounts} />,
      // Neither button in the dialogue is a "never mind", so the corner X is the way out.
      { closeButton: true },
    );

    if (!selection) return;

    if (selection.source === 'connect') {
      await startELinxAdd(selection.paymentMethod);

      return;
    }

    const account = accountRows.allRows.find((row) => getAccountRowToken(row) === selection.token);

    if (!account) return;

    await confirmAddToOperational(toOperationalTarget(account));
  }

  // An account connected through eLinx only becomes addable once the refetched list contains it, so the
  // add flow waits for the row to come back from the API rather than firing off the eLinx payload alone.
  async function startELinxAdd(paymentMethod: 'bank' | 'credit') {
    let link: EventMessageData;

    try {
      link = await eLinxConnect.open({
        PaymentSelectionMethod: paymentMethod,
        ClientControlled: true,
        Language: i18n.language,
      });
    } catch {
      // open() rejects when the user closes the overlay: a cancel, not a failure — nothing to surface.
      return;
    }

    const isCard = link.PaymentMethod === 'creditcard';

    blockingLoader.show();

    let linkedTarget: OperationalTarget | null = null;

    try {
      if (isCard) {
        // A connected card can only show up in the card list.
        const refreshedCards = await refreshCreditCards();
        const linkedCard = refreshedCards.find((card) => card.CreditCardToken === link.Token);

        if (linkedCard) {
          linkedTarget = {
            type: 'Credit Card',
            // The card response carries no currency — a card is charged in whatever currency the
            // funding request asks for, and only CAD card processing is provisioned today.
            currency: CREDIT_CARD_CURRENCY,
            token: link.Token,
            maskedNumber: getMaskedAccountLabel(linkedCard),
            holderName: getAccountHolderLabel(linkedCard),
          };
        }
      } else {
        const refreshedBankAccounts = await refreshBankAccounts();
        const linkedBankAccount = refreshedBankAccounts.find((bankAccount) => bankAccount.Token === link.Token);

        if (linkedBankAccount) {
          linkedTarget = {
            type: 'Bank Account',
            currency: linkedBankAccount.Currency,
            token: link.Token,
            maskedNumber: getMaskedAccountLabel(linkedBankAccount),
            holderName: getAccountHolderLabel(linkedBankAccount),
          };
        }
      }
    } catch {
      // The hooks already logged; the connected account shows up on the next refresh.
    } finally {
      blockingLoader.hide();
    }

    // The confirm dialogue names the account, so it only opens once the account is actually back from
    // the API — and outside the try/finally, so it isn't raised behind the blocking loader.
    if (linkedTarget) await confirmAddToOperational(linkedTarget);
  }

  // The branchy bits of the shared listing arrive as props: which default lookup applies, whether
  // credit cards can be set as default, the permission, and the button label.
  function isDefaultRow(row: AccountRow) {
    if (row.Type === 'Credit Card') return false;

    return defaultOperationalBankAccount.some((obj) => obj.Token === row.Token);
  }

  // GET /account/funding-options is scoped to a single currency per call, so fetch once per
  // currency actually shown (each bank account's currency, plus CAD for the credit card) and
  // merge the results.
  const fundingOptionCurrencies = useMemo(() => {
    const currencies = defaultOperationalBankAccount.map((account) => account.Currency);

    if (response.FundMyAccountCreditCardEnabled) currencies.push(CREDIT_CARD_CURRENCY);

    return Array.from(new Set(currencies));
  }, [defaultOperationalBankAccount, response.FundMyAccountCreditCardEnabled]);

  const fundingOptionCurrenciesKey = fundingOptionCurrencies.join(',');

  useEffect(() => {
    if (fundingOptionCurrencies.length === 0) return;

    const loadFundingOptions = async () => {
      const results = await Promise.allSettled(
        fundingOptionCurrencies.map((currency) => fetchFundingOptions({ Currency: currency })),
      );

      const succeeded = results.filter(
        (result): result is PromiseFulfilledResult<FetchFundingOptionsResponse> => result.status === 'fulfilled',
      );

      setFundingOptions(succeeded.flatMap((result) => result.value.FundingOptions));

      if (succeeded.length < results.length) {
        toast.neutral(t('FundingMethod.fetchOptionsError', { defaultValue: 'Could not load funding fee details.' }));
      }
    };

    loadFundingOptions();
  }, [fundingOptionCurrenciesKey]);

  const goToFundingPage = (pathname: string, token: string, type: MyAccountType, currency: string) => {
    setOpenModule('myAccount');
    setNewCurrencyInfo(currency);
    history.push({ pathname, state: { token, type, currency } });
  };

  return (
    <>
      <Box jc="sb" flx="row">
        <Box flx="col" mb="md" gapSize="8px" width="100%">
          <Title>{getModuleTitle()}</Title>

          <Box jc="sb" gapSize="80px">
            <BodyText>
              {t('Description.operationalAccounts', {
                defaultValue:
                  'Operational accounts are directly linked to your VoPay account. Set a default bank account and credit card per currency for funding and withdrawals.',
              })}
            </BodyText>

            <Button
              id="my-bank-accounts"
              colorScheme="primary"
              disabled={!permissions.ManageDefaultPayments}
              onClick={startAddOperational}
            >
              {t('Button.addOperationalAccount', { defaultValue: 'Add Operational Account' })}
            </Button>
          </Box>
        </Box>
      </Box>

      <Box mt="xl" mb="xl" flx="col" gapSize="16px">
        <Title level="4">{t('Section.defaultFundingMethod', { defaultValue: 'Default Funding Method' })}</Title>

        <DefaultFundingMethods
          defaultBankAccounts={defaultOperationalBankAccount}
          defaultCreditCards={defaultOperationalCreditCard ? [defaultOperationalCreditCard] : []}
          fundingOptions={fundingOptions}
          // Not `!== 'success'`: that stays true forever once the status settles on 'error', leaving a
          // permanent skeleton. refreshDefaultBankAccount toasts the failure, so ending it is enough.
          isLoading={
            isFetchingFundingOptions ||
            isFetchingDefaultCreditCard ||
            defaultBankAccountStatus === 'idle' ||
            defaultBankAccountStatus === 'loading'
          }
          isCreditCardFundingEnabled={response.FundMyAccountCreditCardEnabled}
          onClickFundBankAccount={
            isFranchise
              ? undefined
              : (account) => {
                  goToFundingPage('/fund-my-account', account.Token, 'Bank Account', account.Currency);
                }
          }
          onClickWithdrawBankAccount={
            isFranchise
              ? undefined
              : (account) => {
                  goToFundingPage('/withdraw-my-account', account.Token, 'Bank Account', account.Currency);
                }
          }
          onClickFundCreditCard={
            isFranchise
              ? undefined
              : (card) => {
                  goToFundingPage('/fund-my-account', card.CreditCardToken, 'Credit Card', 'CAD');
                }
          }
          onClickContactSupport={() => {
            history.push({ pathname: '/tickets', state: { openCreateTicket: true } });
          }}
          onClickBankAccountDetails={(account) => {
            listing.openAccountDetails(account.Token);
          }}
          onClickCreditCardDetails={(card) => {
            listing.openAccountDetails(card.CreditCardToken);
          }}
        />
      </Box>

      <Box flx="col" gapSize="16px">
        <Title level="4">{t('Section.accounts', { defaultValue: 'Accounts' })}</Title>

        <Card style={{ padding: '16px' }}>
          <AccountListingPanel
            listing={listing}
            accountListingType="Operational"
            canSetDefault={!!permissions.ManageDefaultPayments}
            canDelete={!!permissions.ManageDefaultPayments}
            canSetDefaultCreditCards={false}
            setDefaultLabel={t('Table.setAsDefaultOperational', { defaultValue: 'Set as Default Operational' })}
            isDefaultRow={isDefaultRow}
            onSetDefault={confirmChangeDefault}
            isLoading={
              bankAccountsStatus === 'idle' || bankAccountsStatus === 'loading' || creditCardsStatus === 'loading'
            }
            refreshError={
              isShowingRefreshError
                ? t('refreshError', {
                    defaultValue: "We couldn't refresh your accounts. The list below may be out of date.",
                  })
                : undefined
            }
            loadError={
              isShowingLoadError
                ? t('loadError', { defaultValue: "We couldn't load your accounts. Please try again." })
                : undefined
            }
            renderDetailsActions={({ disabled }) => {
              const account = listing.selectedAccount;

              if (isFranchise || !account?.IsDefault) return null;

              return (
                <Box flx="row" gap="sm" height="auto">
                  <Button
                    id="operational-account-drawer-add-funds"
                    colorScheme="secondary"
                    size="large"
                    iconLeft="arrow_upward"
                    disabled={disabled}
                    onClick={() => {
                      setOpenModule('myAccount');

                      history.push({
                        pathname: '/fund-my-account',
                        state: {
                          token: account.Type === 'Bank Account' ? account.Token : '',
                          type: account.Type,
                          currency: account.Currency,
                          cardToken: account.Type === 'Credit Card' ? account.CreditCardToken : undefined,
                          account,
                        },
                      });
                    }}
                  >
                    {t('Button.addFunds', { defaultValue: 'Add Funds' })}
                  </Button>

                  {account.Type !== 'Credit Card' && (
                    <Button
                      id="operational-account-drawer-withdraw"
                      colorScheme="secondary"
                      size="large"
                      iconLeft="arrow_downward"
                      disabled={disabled}
                      onClick={() => {
                        setOpenModule('myAccount');

                        // Withdraw is bank-only (the button is hidden for a card row), so the token
                        // here is always the bank one.
                        history.push({
                          pathname: '/withdraw-my-account',
                          state: {
                            token: account.Type === 'Bank Account' ? account.Token : '',
                            type: account.Type,
                            currency: account.Currency,
                            account,
                          },
                        });
                      }}
                    >
                      {t('Button.withdraw', { defaultValue: 'Withdraw' })}
                    </Button>
                  )}
                </Box>
              );
            }}
            onAccountDeleted={async () => {
              blockingLoader.show();

              try {
                if (listing.selectedAccount?.Type === 'Credit Card') {
                  await refreshCreditCards();
                } else {
                  await refreshBankAccounts();
                }
              } catch {
                // The hooks already logged; the deleted row disappears on the next refresh.
              } finally {
                blockingLoader.hide();
              }
            }}
          />
        </Card>
      </Box>
    </>
  );
}
